/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable unused-imports/no-unused-vars */
import { BitkitCheckbox, BitkitCodeSnippet, BitkitExpandableCard, BitkitMarkdown } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { useToken } from '@chakra-ui/react/styled-system';
import { Table } from '@chakra-ui/react/table';
import { Text } from '@chakra-ui/react/text';
import { ComponentProps, useMemo } from 'react';
import { Children, cloneElement, isValidElement, ReactNode } from 'react';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import SyntaxHighlighter from '@/components/SyntaxHighlighter/SyntaxHighlighter';

// oneLight's palette is tuned for a light surface; remapping each colour to a semantic token makes the highlighting follow the color mode.
const ONE_LIGHT_TOKENS: Record<string, string> = {
  'hsl(230, 8%, 24%)': 'text/body',
  'hsl(230, 4%, 64%)': 'text/tertiary',
  'hsl(35, 99%, 36%)': 'status/warning/text',
  'hsl(301, 63%, 40%)': 'status/ai/text',
  'hsl(5, 74%, 59%)': 'status/critical/text',
  'hsl(344, 84%, 43%)': 'status/critical/text',
  'hsl(119, 34%, 47%)': 'status/success/text',
  'hsl(221, 87%, 60%)': 'status/info/text',
  'hsl(198, 99%, 37%)': 'text/link',
};
const ONE_LIGHT_COLORS = Object.keys(ONE_LIGHT_TOKENS);
const CODE_BLOCK_TOKENS = ['background/secondary', ...Object.values(ONE_LIGHT_TOKENS)];

const useBitkitOneLight = () => {
  const resolvedColors = useToken('colors', CODE_BLOCK_TOKENS);

  return useMemo(() => {
    const [surfaceColor, ...tokenColors] = resolvedColors;
    const style = Object.fromEntries(
      Object.entries(oneLight).map(([selector, rules]) => {
        const index = ONE_LIGHT_COLORS.indexOf(rules.color ?? '');
        return [selector, index === -1 ? rules : { ...rules, color: tokenColors[index] }];
      }),
    );
    const { margin, padding, ...pre } = style['pre[class*="language-"]'];
    return {
      ...style,
      'code[class*="language-"]': { ...style['code[class*="language-"]'], background: surfaceColor },
      'pre[class*="language-"]': { ...pre, background: surfaceColor },
    };
  }, [resolvedColors]);
};

const CodeBlock = ({ language, children }: { language: string; children: ReactNode }) => {
  const style = useBitkitOneLight();

  return (
    <SyntaxHighlighter
      style={style}
      language={language}
      customStyle={{
        padding: '1rem',
        borderRadius: '0.5rem',
        fontSize: '0.75rem',
      }}
    >
      {String(children)}
    </SyntaxHighlighter>
  );
};

const components: Record<string, (c: any) => ReactNode> = {
  table: ({ node, ...props }) => <Table.Root {...props} />,
  thead: ({ node, ...props }) => <Table.Header {...props} />,
  tbody: ({ node, ...props }) => <Table.Body {...props} />,
  tr: ({ node, ...props }) => <Table.Row {...props} />,
  th: ({ node, ...props }) => <Table.ColumnHeader {...props} />,
  td: ({ node, ...props }) => <Table.Cell {...props} />,
  input: ({ checked, disabled, type }) => {
    if (type === 'checkbox') {
      return (
        <BitkitCheckbox checked={!!checked} aria-label="Task list item" state={disabled ? 'disabled' : 'readOnly'} />
      );
    }
    return null;
  },
  details: ({ open, children }) => {
    // Find the summary element to use as the card title
    let title: ReactNode = null;
    let contentChildren = Children.toArray(children);
    Children.forEach(children, (child, index) => {
      if (isValidElement(child) && child.key === 'summary-0') {
        title = <Text as="span">{(child.props as any).children}</Text>;
        contentChildren = contentChildren.filter((_, i) => i !== index);
      }
    });
    return (
      <BitkitExpandableCard defaultExpanded={!!open} elevation={false} title={title}>
        <Box display="flex" flexDirection="column" gap="16">
          {contentChildren}
        </Box>
      </BitkitExpandableCard>
    );
  },
  summary: () => {
    // Return null as we're handling summary content in the details component
    return null;
  },
  code: ({ className, children }) => {
    const language = /language-(\w+)/.exec(className || '');
    const variant = /variant-(\w+)/.exec(className || '')?.[1] || 'inline';
    if (language) {
      return <CodeBlock language={language[1]}>{children}</CodeBlock>;
    }
    if (variant === 'inline') {
      return (
        <BitkitCodeSnippet interactive size="md" variant="inline">
          {children as string}
        </BitkitCodeSnippet>
      );
    }
    if (variant === 'multi') {
      // v1 `multi` kept lines whole behind a horizontal scrollbar; `wrapLines={false}` is the v2 equivalent.
      return (
        <BitkitCodeSnippet size="md" variant="multi" wrapLines={false}>
          {children as string}
        </BitkitCodeSnippet>
      );
    }
    return (
      <BitkitCodeSnippet size="md" variant="single">
        {children as string}
      </BitkitCodeSnippet>
    );
  },
  pre: ({ children }) => {
    const newChildren = Children.map(children, (child) => {
      if (isValidElement(child) && !(child.props as any).className) {
        return cloneElement(child, {
          className: 'variant-multi',
        } as { className: string });
      }
      return child;
    });
    return <>{newChildren}</>;
  },
};

type RehypePlugins = ComponentProps<typeof BitkitMarkdown>['rehypePlugins'];

const plugins: RehypePlugins = [remarkGfm, rehypeRaw, [rehypeSanitize, defaultSchema]];

type MarkdownBlockProps = Omit<ComponentProps<typeof BitkitMarkdown>, 'children' | 'components' | 'rehypePlugins'> & {
  md: string;
};

// `size` defaults to `lg` here, not to BitkitMarkdown's own `md`, so the call sites that never
// passed one keep the size the v1 MarkdownContent gave them.
const MarkdownBlock = ({ md, size = 'lg', ...props }: MarkdownBlockProps) => (
  <BitkitMarkdown components={components} rehypePlugins={plugins} size={size} {...props}>
    {md}
  </BitkitMarkdown>
);

export default MarkdownBlock;
